<!--
  THE REFERRAL NETWORK (tasks/referrals.md §5.2), as PANELS in the MedicAI Network tab: settings-container holds the
  heading, the hint and the one accordion, and these sit under the colleague panels (owner, 2026-09-21: one settings page,
  not two headings). Contacts above only hold details; these are LINKS, which can receive what the practice shares.

  ONE section, REFERRAL NETWORK (owner, 2026-09-22; renamed from Medical professionals the same day), in place of Referral clinicians, Linked practices and
  Invitations sent: every link in one list, each chip coloured and iconed by its kind - Clinician (outside MedicAI),
  Practice (another MedicAI practice), Invitation (an email nobody has accepted yet: ONE kind whichever way it went, since
  the server does not say until they accept). The filter chips above the list are also its key, and narrow it to a kind.

  A practice that has ASKED to link with you is the exception: accepting and declining are two decisions, and a chip has
  room for neither. Those keep a row of their own (kind Request), above the chips, until the practice owner answers.
  `mat-chip-grid` for a list that can be acted on, `mat-chip-set` for one that cannot (ended links).
-->
<!-- The list's load state, shown inside the section. -->
<ng-template #listState>
    <div class="rns-alert" *ngIf="listError" data-test="list-error">
        <mat-icon>error_outline</mat-icon>
        <div>{{ listError }}</div>
    </div>
    <mat-progress-bar mode="indeterminate" *ngIf="isLoading" data-test="list-loading"></mat-progress-bar>
</ng-template>

<!-- Opens by itself once when another practice is waiting on an answer: a request must not sit in a closed panel. -->
<mat-expansion-panel [(expanded)]="sectionExpanded" data-test="section-professionals" aria-label="Referral network">
    <mat-expansion-panel-header>
        <mat-panel-title>
            REFERRAL NETWORK
            <span class="rns-waiting" *ngIf="practiceRequests.length" data-test="practice-requests-count">{{ practiceRequests.length }} to answer</span>
            <mat-icon class="expansion-header-icon" data-test="invite" (click)="openInviteDialog($event)" color="primary"
                matTooltip="Invite someone to your referral network by email">add</mat-icon>
            <mat-icon class="expansion-header-icon" *ngIf="practiceLinksEnabled" data-test="add-practice"
                (click)="openPracticeLinkDialog($event)" color="primary"
                matTooltip="Enter another practice's referral code">dialpad</mat-icon>
        </mat-panel-title>
    </mat-expansion-panel-header>

    <p class="hint rns-panel-hint">Clinicians outside MedicAI<ng-container *ngIf="practiceLinksEnabled"> and other MedicAI
        practices</ng-container> you can refer patients to. They see only the records you share.</p>
    <ng-container *ngTemplateOutlet="listState"></ng-container>

    <p class="hint rns-empty" *ngIf="!isLoading && !openItems.length">Nobody in your referral network yet. Use + to invite
        someone by email<ng-container *ngIf="practiceLinksEnabled">, or enter another practice's referral code</ng-container>.</p>

    <ng-container *ngIf="openItems.length">
        <!-- The kinds, each with its count: also the key to the chip colours below. Empty kinds are left out. -->
        <mat-chip-listbox class="rns-filters" aria-label="Show one kind of referral network entry" data-test="kind-filters"
            (change)="setFilter($event.value)">
            <mat-chip-option *ngFor="let f of filterOptions; trackBy: trackByKey" [value]="f.key"
                [selected]="activeFilter === f.key" [ngClass]="'rns-filter rns-kind--' + f.key"
                [attr.data-test]="'filter-' + f.key">
                <mat-icon matChipAvatar *ngIf="f.key !== 'all'">{{ kindIcon[f.key] }}</mat-icon>
                {{ f.label }}<span class="rns-filter__count" *ngIf="f.key !== 'all'">{{ f.count }}</span>
            </mat-chip-option>
        </mat-chip-listbox>

        <mat-form-field class="full-width rns-search" appearance="outline" *ngIf="openItems.length > searchFrom">
            <mat-label>Search</mat-label>
            <mat-icon matPrefix>search</mat-icon>
            <input matInput [(ngModel)]="query" autocomplete="off" placeholder="Name, email or practice" data-test="search">
        </mat-form-field>

        <!-- Waiting on YOUR practice owner: two decisions, so these keep a row until they are answered. -->
        <div class="rns-request" *ngFor="let link of visibleRequests; trackBy: trackById" data-test="practice-request">
            <span class="rns-kind-tag rns-kind--request"><mat-icon>{{ kindIcon.request }}</mat-icon>Request</span>
            <div class="rns-request__body">
                <div class="rns-request__name">{{ displayName(link) }}</div>
                <div class="rns-request__status">{{ statusLabel(link) }} · {{ link.requestedAt | date: 'd MMM y' }}</div>
            </div>
            <ng-container *ngIf="viewerCanRespond; else ownerAnswers">
                <button mat-stroked-button (click)="respond(link, 'decline')" [disabled]="respondingLinkId === link.id" data-test="decline-link">Decline</button>
                <button mat-flat-button color="primary" (click)="respond(link, 'accept')" [disabled]="respondingLinkId === link.id" data-test="accept-link">Accept</button>
            </ng-container>
            <ng-template #ownerAnswers>
                <span class="hint" data-test="owner-answers">Waiting for the practice owner</span>
            </ng-template>
        </div>

        <mat-form-field class="full-width" appearance="outline" *ngIf="visibleChips.length">
            <mat-label>{{ activeFilterLabel }}</mat-label>
            <mat-chip-grid #professionalChips aria-label="Your referral network">
                <mat-chip-row *ngFor="let link of visibleChips; trackBy: trackById" [editable]="false"
                    [matTooltip]="tooltipFor(link)" [ngClass]="'rns-chip rns-kind--' + kindOf(link)"
                    [class.rns-chip--pending]="link.status === 'pending'"
                    [attr.aria-label]="kindLabel[kindOf(link)] + ': ' + displayName(link)"
                    [attr.data-test]="'chip-' + kindOf(link)">
                    <mat-icon matChipAvatar>{{ kindIcon[kindOf(link)] }}</mat-icon>
                    {{ displayName(link) }}
                    <button matChipRemove *ngIf="canEnd(link)" [disabled]="endingLinkId === link.id"
                        [attr.aria-label]="endLabel(link)" (click)="endLink(link)" data-test="end-link">
                        <mat-icon>cancel</mat-icon>
                    </button>
                </mat-chip-row>
            </mat-chip-grid>
            <input [matChipInputFor]="professionalChips" disabled />
            <mat-hint>Hover a chip for their details and the state of the link. The × cancels an invitation or request, or ends a link.</mat-hint>
        </mat-form-field>

        <p class="hint rns-empty" *ngIf="!visibleRequests.length && !visibleChips.length" data-test="no-match">Nothing matches.
            <a href="" (click)="clearFilters($event)">Show everyone</a></p>
    </ng-container>

    <!-- The code another MedicAI practice enters to link with you (§19): only where practice links are on. -->
    <ng-container *ngIf="practiceLinksEnabled">
        <div class="rns-code" data-test="practice-code">
            <div class="rns-code__text">
                <div class="rns-code__name">Your referral code</div>
                <div class="hint">Give it to a practice that wants to link with you. Only your administrators see it.</div>
            </div>
            <ng-container *ngIf="practiceCode; else showCode">
                <span class="rns-code__value" data-test="practice-code-value">{{ practiceCode }}</span>
                <button mat-icon-button (click)="copyPracticeCode()" matTooltip="Copy"><mat-icon>content_copy</mat-icon></button>
                <button mat-button (click)="showCodeEmail = !showCodeEmail" data-test="email-code-toggle">Email code</button>
                <button mat-button (click)="renewPracticeCode()" [disabled]="codeBusy" data-test="renew-code">New code</button>
            </ng-container>
            <ng-template #showCode>
                <button mat-stroked-button (click)="showPracticeCode()" [disabled]="codeBusy" data-test="show-code">Show code</button>
            </ng-template>
            <button mat-button color="primary" (click)="openPracticeLinkDialog()" data-test="enter-code">Enter a code</button>
        </div>

        <!-- Email the code to a practice you know is on MedicAI (owner, 2026-09-18). -->
        <div class="rns-code-email" *ngIf="showCodeEmail && practiceCode" data-test="email-code">
            <mat-form-field appearance="outline" class="rns-code-email__field">
                <mat-label>Their email address</mat-label>
                <input matInput type="email" [(ngModel)]="codeEmail" (keyup.enter)="emailPracticeCode()" autocomplete="off" data-test="email-code-address">
                <mat-hint>We'll email them your code, with how to enter it in MedicAI.</mat-hint>
            </mat-form-field>
            <button mat-flat-button color="primary" (click)="emailPracticeCode()" [disabled]="emailingCode || !codeEmail.trim()" data-test="email-code-send">
                {{ emailingCode ? 'Sending…' : 'Send' }}
            </button>
        </div>
    </ng-container>
</mat-expansion-panel>

<mat-expansion-panel *ngIf="closedLinks.length" data-test="section-closed">
    <mat-expansion-panel-header>
        <mat-panel-title>ENDED AND DECLINED ({{ closedLinks.length }})</mat-panel-title>
    </mat-expansion-panel-header>
    <!-- Read-only, so a set and not a grid: there is nothing here to act on. -->
    <mat-chip-set class="rns-closed" aria-label="Links that have ended or were declined">
        <mat-chip *ngFor="let link of closedLinks; trackBy: trackById" [disableRipple]="true"
            [matTooltip]="tooltipFor(link)" data-test="closed-row">
            <mat-icon matChipAvatar>{{ iconFor(link) }}</mat-icon>
            {{ displayName(link) }}
        </mat-chip>
    </mat-chip-set>
</mat-expansion-panel>
